import {useCallback, useEffect, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
const {axios} = config

// Roadmap #11 (2026-09-23): practitioner/business specials. They create
// them in the app and they go live immediately - this page lets Enforma
// see every special and remove (or restore) any of them.
const STATUS = {
  active: ['Active', 'success'],
  scheduled: ['Scheduled', 'info'],
  expired: ['Expired', 'secondary'],
  ended: ['Ended by owner', 'secondary'],
  used_up: ['Used up', 'secondary'],
  removed: ['Removed by Enforma', 'danger'],
}

const describe = (p) => {
  if (p.kind === 'percent') return `${p.value}% off`
  if (p.kind === 'amount') return `${p.value} off`
  return 'Announcement'
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '—')

const Specials = () => {
  const [rows, setRows] = useState([])
  const [status, setStatus] = useState('active')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/promotions`, {params: {status, search}})
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load specials.')
    } finally {
      setLoading(false)
    }
  }, [status, search])

  useEffect(() => {
    const t = setTimeout(load, 250)
    return () => clearTimeout(t)
  }, [load])

  const act = async (p, action) => {
    if (action === 'remove' && !window.confirm(`Remove "${p.title}" from ${p.owner?.name}? It stops applying immediately.`)) return
    setBusyId(p._id)
    try {
      await axios.put(`${BASE_URL}/admin/promotions/${p._id}/${action}`)
      await load()
    } catch (e) {
      setError('Could not update this special.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>Specials</h3>
        </div>
        <div className='card-toolbar gap-3'>
          <input
            className='form-control form-control-solid w-250px'
            placeholder='Search title, practitioner, code'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className='form-select form-select-solid w-200px' value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value='all'>All</option>
            {Object.entries(STATUS).map(([k, [label]]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='card-body border-top p-9'>
        <p className='text-muted'>
          Practitioners and businesses create specials in the app and they go live immediately.
          Remove any special here to stop it applying straight away.
        </p>
        {error && <div className='alert alert-danger py-3'>{error}</div>}
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-4'>
            <thead>
              <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                <th>Special</th>
                <th>Practitioner / Business</th>
                <th>Offer</th>
                <th>Applies to</th>
                <th>Dates</th>
                <th>Used</th>
                <th>Status</th>
                <th className='text-end'></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className='text-muted'>Loading...</td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className='text-muted'>No specials found.</td>
                </tr>
              )}
              {!loading &&
                rows.map((p) => {
                  const [label, color] = STATUS[p.status] || [p.status, 'secondary']
                  return (
                    <tr key={p._id}>
                      <td>
                        <div className='fw-bold'>{p.title}</div>
                        {p.description && <div className='text-muted fs-7'>{p.description}</div>}
                      </td>
                      <td>
                        <div>{p.owner?.name}</div>
                        <div className='text-muted fs-7'>{p.owner?.primaryLocation}</div>
                      </td>
                      <td>
                        {describe(p)}
                        {p.code && <div className='text-muted fs-7'>Code: {p.code}</div>}
                      </td>
                      <td>
                        {p.appliesToAll
                          ? 'Everything'
                          : `${(p.classes || []).length} class(es), ${(p.services || []).length} service(s)`}
                      </td>
                      <td className='fs-7'>
                        {fmt(p.startsAt)} – {p.endsAt ? fmt(p.endsAt) : 'no end'}
                      </td>
                      <td>
                        {p.redemptionCount || 0}
                        {p.maxRedemptions ? ` / ${p.maxRedemptions}` : ''}
                      </td>
                      <td>
                        <span className={`badge badge-light-${color}`}>{label}</span>
                      </td>
                      <td className='text-end'>
                        {p.status === 'removed' ? (
                          <button className='btn btn-sm btn-light' disabled={busyId === p._id} onClick={() => act(p, 'restore')}>
                            Restore
                          </button>
                        ) : (
                          <button className='btn btn-sm btn-light-danger' disabled={busyId === p._id} onClick={() => act(p, 'remove')}>
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Specials
